/**
 * Stores all context data accessible to forms.
 */
import { RootState } from '@/store/types'
import { forEach, get, merge } from 'lodash'
import Vue from 'vue'
import { Module } from 'vuex'

/**
 * Modified lodash baseSet function, modifies object to include path and assigns the value to the final key.
 *
 * @param object The object to modify.
 * @param key The key of object to assign.
 * @param value The value to assign.
 */
const setWithVue = (object: Record<string, any>, path: string, value: any) => {
  const paths = path.split('.')
  const lastIndex = paths.length - 1
  let current = object

  for (const [index, key] of paths.entries()) {
    if (index === lastIndex) {
      Vue.set(current, key, value)
      break
    }
    let next = current[key]
    if (next === undefined) {
      next = Vue.set(current, key, {})
    }
    current = next
  }

  return object
}

const namespaced: boolean = true

export interface ContextState {
  context: Record<string, any>
  readonly: Record<string, any>
}

export const context: Module<ContextState, RootState> = {
  namespaced,
  state: {
    context: {},
    readonly: {}
  },
  getters: {
    getContext: state => merge({}, state.context, state.readonly),
    getVariable: (_, getters) => (path: string) => get(getters['getContext'], path)
  },
  mutations: {
    setVariable: (state, { path, value }) => setWithVue(state.context, path, value),
    setVariables: (state, { variables }: { variables: Record<string, any> }) => {
      forEach(variables, (value, path) => setWithVue(state.context, path, value))
    },
    setReadonlyVariables: (state, { variables }: { variables: Record<string, any> }) => {
      forEach(variables, (value, path) => setWithVue(state.readonly, path, value))
    },
    resetState: (state, options?: { readonly: boolean }) => {
      const { readonly = true } = options || {}
      Vue.set(state, 'context', {})
      if (readonly) {
        Vue.set(state, 'readonly', {})
      }
    },
  }
}
